<template>
  <component :is="layoutComponent" v-if="layoutComponent">
    <router-view v-slot="{ Component, route }">
      <component :is="Component" :key="route.fullPath" />
    </router-view>
  </component>
  <div v-else class="loading-container">
    <div class="loading-spinner"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineAsyncComponent, shallowRef } from 'vue'
import { useRoute } from 'vue-router'
import { useTemplateStore } from './store/modules/template'

const route = useRoute()
const templateStore = useTemplateStore()
const layoutComponent = shallowRef(null)

const loadLayout = async () => {
  let template = templateStore.currentTemplate || 'default'
  
  const currentPath = route.path
  const moduleName = currentPath.split('/')[1]
  
  if (moduleName && templateStore.moduleTemplates[moduleName]) {
    template = templateStore.moduleTemplates[moduleName]
  }
  
  try {
    const module = await import(`./themes/${template}/layouts/Default.vue`)
    layoutComponent.value = module.default
  } catch (error) {
    console.warn(`Layout for theme ${template} not found, using default`)
    const module = await import('./themes/default/layouts/Default.vue')
    layoutComponent.value = module.default
  }
}

watch(() => route.path, () => {
  loadLayout()
})

watch(() => templateStore.loaded, (loaded) => {
  if (loaded) {
    loadLayout()
  }
})

onMounted(() => {
  if (templateStore.loaded) {
    loadLayout()
  } else {
    templateStore.fetchTemplateConfig().then(() => {
      loadLayout()
    })
  }
})
</script>

<style>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #00d1b2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-leave-to {
  transform: translateX(-100%);
}

.zoom-enter-active,
.zoom-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
  transform: scale(0.9);
  opacity: 0;
}
</style>
